/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid
} from "@mui/material";
import {
    Close,
    Download,
    Print,
    Info,
} from "@mui/icons-material";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

interface PerformanceMetric {
    name: string;
    duration?: number;
    threshold?: number;
    metadata?: Record<string, any>;
    excess?: number;
}

interface ReportData {
    totalMetrics: number;
    averageDuration: number;
    allOperations: PerformanceMetric[];
    slowestOperations: PerformanceMetric[];
    thresholdViolations: PerformanceMetric[];
    memoryUsage: string | Record<string, string>;
}

const PerformanceDashboard: React.FC = () => {
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [selectedMetric, setSelectedMetric] = useState<PerformanceMetric | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        // Parse report data from URL parameters
        const params = new URLSearchParams(window.location.search);
        const reportParam = params.get("report");

        if (reportParam) {
            try {
                const decoded = decodeURIComponent(reportParam);
                const parsed: ReportData = JSON.parse(decoded);
                setReportData(parsed);
            } catch (error) {
                console.error("Failed to parse report data:", error);
            }
        }
        setLoading(false);
    }, []);

    const downloadJSON = () => {
        if (!reportData) return;
        const dataStr = JSON.stringify(reportData, null, 2);
        const blob = new Blob([dataStr], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `performance-report-${new Date().toISOString()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    backgroundColor: "#f5f5f5",
                }}
            >
                <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h4" sx={{ mb: 2 }}>
                        ⏳
                    </Typography>
                    <Typography variant="h5">Loading Performance Report...</Typography>
                </Box>
            </Box>
        );
    }

    if (!reportData) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    backgroundColor: "#f5f5f5",
                }}
            >
                <Box sx={{ textAlign: "center", maxWidth: 600, p: 3 }}>
                    <Typography variant="h4" sx={{ mb: 2 }}>
                        ❌
                    </Typography>
                    <Typography variant="h5" gutterBottom>
                        No Report Data Found
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        This page should be opened using the "View Performance Report"
                        button from the Image Viewer.
                    </Typography>
                </Box>
            </Box>
        );
    }

    const {
        totalMetrics,
        averageDuration,
        allOperations = [],
        slowestOperations = [],
        thresholdViolations = [],
        memoryUsage,
    } = reportData;

    // Calculate performance score
    // const performanceScore = Math.round(((totalMetrics - thresholdViolations.length) / totalMetrics) * 100);

    // Doughnut chart for performance score
    // const scoreChartData = {
    //     labels: ["Performance", "Issues"],
    //     datasets: [
    //         {
    //             data: [performanceScore, 100 - performanceScore],
    //             backgroundColor: [
    //                 performanceScore > 70
    //                     ? "rgba(76, 175, 80, 0.8)"
    //                     : performanceScore > 50
    //                         ? "rgba(255, 152, 0, 0.8)"
    //                         : "rgba(244, 67, 54, 0.8)",
    //                 "rgba(200, 200, 200, 0.3)",
    //             ],
    //             borderWidth: 2,
    //         },
    //     ]
    // };

    // const scoreChartOptions = {
    //     responsive: true,
    //     maintainAspectRatio: false,
    //     plugins: {
    //         legend: { display: false },
    //         tooltip: { enabled: false }
    //     }
    // };

    const COLORS = [
        "#FF6B6B",
        "#4ECDC4",
        "#45B7D1",
        "#FFA07A",
        "#98D8C8",
        "#F7DC6F",
        "#BB8FCE",
        "#85C1E2",
    ];

    const getStatusColor = (duration?: number, threshold?: number): string => {
        if (!threshold || !duration) return "#4CAF50";
        const ratio = duration / threshold;
        if (ratio > 1.5) return "#f44336";
        if (ratio > 1.0) return "#FF9800";
        return "#4CAF50";
    };

    const formatMemory = (memoryData: string | Record<string, string>): string => {
        if (typeof memoryData === "string") return memoryData;
        return Object.entries(memoryData)
            .map(([key, value]) => `${key.replace(/([A-Z])/g, " $1").trim()}: ${value}`)
            .join("\n");
    };

    return (
        <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
            {/* Header */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Performance Metrics Report
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Generated: {new Date().toLocaleString()}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={downloadJSON}
                            sx={{ textTransform: "none" }}
                        >
                            JSON
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Print />}
                            onClick={() => window.print()}
                            color="secondary"
                            sx={{ textTransform: "none" }}
                        >
                            Print
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                            >
                                Performance Score
                            </Typography>
                            <Box
                                sx={{
                                    position: "relative",
                                    height: 120,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Doughnut data={scoreChartData} options={scoreChartOptions} />
                                <Typography
                                    variant="h3"
                                    sx={{
                                        position: "absolute",
                                        fontWeight: "bold",
                                        color:
                                            performanceScore > 70
                                                ? "#4CAF50"
                                                : performanceScore > 50
                                                    ? "#FF9800"
                                                    : "#f44336",
                                    }}
                                >
                                    {performanceScore}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid> */}

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                            >
                                Total Metrics
                            </Typography>
                            <Typography
                                variant="h3"
                                color="primary"
                                sx={{ fontWeight: "bold" }}
                            >
                                {totalMetrics}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                operations tracked
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                            >
                                Average Duration
                            </Typography>
                            <Typography
                                variant="h3"
                                sx={{ fontWeight: "bold", color: "#9C27B0" }}
                            >
                                {averageDuration.toFixed(0)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                milliseconds
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                            >
                                Threshold Violations
                            </Typography>
                            <Typography
                                variant="h3"
                                color="error"
                                sx={{ fontWeight: "bold" }}
                            >
                                {thresholdViolations.length}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                operations exceeded
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid> */}
            </Grid>

            {/* Memory Usage */}
            {memoryUsage && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        💾 Memory Usage
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                        <Typography
                            component="pre"
                            sx={{
                                fontFamily: "monospace",
                                fontSize: "0.875rem",
                                whiteSpace: "pre-wrap",
                                m: 0,
                            }}
                        >
                            {formatMemory(memoryUsage)}
                        </Typography>
                    </Paper>
                </Paper>
            )}

            {/* Slowest Operations Table */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Operation Duration Analysis
                </Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell>Operation</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Threshold</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {allOperations.map((metric, index) => {
                                const statusColor = getStatusColor(metric.duration, metric.threshold);
                                const exceeded = metric.threshold && metric.duration && metric.duration > metric.threshold;

                                return (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "50%",
                                                    backgroundColor: COLORS[index % COLORS.length],
                                                    color: "white",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {index + 1}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {metric.name.replace(/_/g, " ")}
                                            </Typography>
                                            {metric.metadata && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {Object.keys(metric.metadata).length} metadata entries
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: "bold", color: statusColor }}
                                            >
                                                {metric.duration?.toFixed(2) || 0}ms
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {metric.threshold ? `${metric.threshold}ms` : "N/A"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    exceeded
                                                        ? `⚠️ +${(
                                                            (metric.duration || 0) - (metric.threshold || 0)
                                                        ).toFixed(2)}ms`
                                                        : "✓ OK"
                                                }
                                                color={exceeded ? "error" : "success"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => setSelectedMetric(metric)}
                                            >
                                                <Info />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Metric Detail Dialog */}
            <Dialog
                open={!!selectedMetric}
                onClose={() => setSelectedMetric(null)}
                maxWidth="md"
                fullWidth
            >
                {selectedMetric && (
                    <>
                        <DialogTitle>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Typography variant="h6">
                                    {selectedMetric.name.replace(/_/g, " ")}
                                </Typography>
                                <IconButton onClick={() => setSelectedMetric(null)}>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={6}>
                                    <Paper
                                        elevation={0}
                                        sx={{ p: 2, backgroundColor: "#f5f5f5" }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            Duration
                                        </Typography>
                                        <Typography variant="h4" color="primary" fontWeight="bold">
                                            {selectedMetric.duration?.toFixed(2) || 0}ms
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper
                                        elevation={0}
                                        sx={{ p: 2, backgroundColor: "#f5f5f5" }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            Threshold
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            sx={{ color: "#FF9800", fontWeight: "bold" }}
                                        >
                                            {selectedMetric.threshold || "N/A"}ms
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor:
                                        selectedMetric.threshold &&
                                            selectedMetric.duration &&
                                            selectedMetric.duration > selectedMetric.threshold
                                            ? "#ffebee"
                                            : "#e8f5e9",
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                    Status
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color:
                                            selectedMetric.threshold &&
                                                selectedMetric.duration &&
                                                selectedMetric.duration > selectedMetric.threshold
                                                ? "#c62828"
                                                : "#2e7d32",
                                    }}
                                >
                                    {selectedMetric.threshold &&
                                        selectedMetric.duration &&
                                        selectedMetric.duration > selectedMetric.threshold
                                        ? `⚠️ Exceeded by ${(
                                            (selectedMetric.duration || 0) -
                                            (selectedMetric.threshold || 0)
                                        ).toFixed(2)}ms`
                                        : "✓ Within performance limits"}
                                </Typography>
                            </Paper>

                            {selectedMetric.metadata && (
                                <>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                        gutterBottom
                                    >
                                        Metadata
                                    </Typography>
                                    <Paper
                                        elevation={0}
                                        sx={{ p: 2, backgroundColor: "#f5f5f5" }}
                                    >
                                        <Typography
                                            component="pre"
                                            sx={{
                                                fontFamily: "monospace",
                                                fontSize: "0.813rem",
                                                lineHeight: 1.5,
                                                m: 0,
                                                whiteSpace: "pre-wrap",
                                                overflow: "auto",
                                            }}
                                        >
                                            {JSON.stringify(selectedMetric.metadata, null, 2)}
                                        </Typography>
                                    </Paper>
                                </>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setSelectedMetric(null)}
                                variant="contained"
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default PerformanceDashboard;
